import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Eye, EyeOff } from 'lucide-react';

import { Button } from './ui/button';
import { copyText } from '../lib/clipboard';
import { cn, EYEBROW } from '../lib/utils';

interface CredentialFieldProps {
  /** Uppercase eyebrow label, e.g. "Client secret". */
  label: string;
  value: string;
  /** Secrets render masked; ids render in the clear. */
  secret?: boolean;
  /**
   * Whether a masked secret offers an eye toggle.
   *
   * Off for credentials that stay retrievable — an app or template secret can
   * be copied as often as the user likes, so there is never a reason to put it
   * on screen where a screen-share or a shoulder would catch it. Copy is the
   * only way out of the field.
   */
  revealable?: boolean;
  /**
   * Focus the copy button on mount. For a credential that has just been
   * created in place: the button that created it unmounts under the cursor,
   * and focus moves to the result instead of falling to the page.
   */
  autoFocusCopy?: boolean;
  className?: string;
}

const MASK = '•'.repeat(24);

/**
 * The label as it reads inside a sentence: 'Client secret' → 'client secret',
 * but an acronym keeps its case ('ID' stays 'ID', 'URI' stays 'URI') — the old
 * blanket lower-casing produced 'Copy client id'.
 */
export function inlineLabel(label: string): string {
  const [first = '', ...rest] = label.split(' ');
  const isAcronym = first.length > 1 && first === first.toUpperCase() && /[A-Z]/.test(first);
  const head = isAcronym ? first : first.charAt(0).toLowerCase() + first.slice(1);
  return [head, ...rest].join(' ');
}

/**
 * The single treatment for client ids, secrets, and connection strings:
 * eyebrow label, mono well, optional reveal, copy with toast + SR announcement.
 *
 * A `secret` field with `revealable={false}` can only ever be copied, never
 * read — the mask has no escape hatch.
 */
export const CredentialField: React.FC<CredentialFieldProps> = ({
  label,
  value,
  secret = false,
  revealable = true,
  autoFocusCopy = false,
  className,
}) => {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (autoFocusCopy) copyButtonRef.current?.focus();
    // Mount only: a re-render must not pull focus back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopy = async () => {
    if (await copyText(value, { label, noun: inlineLabel(label) })) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  // A value shown in clear wraps so it can be READ in full (a long repository
  // URI); only a mask truncates, because there is nothing behind it to read.
  const showsClear = !secret || revealed;

  return (
    <div className={cn('min-w-0', className)}>
      <p className={cn('mb-1.5', EYEBROW)}>
        {label}
      </p>
      {/* Top-aligned: py-1.5 + a 16px line = 28px, the icon-xs buttons' height,
          so the buttons sit level with the first line of a wrapped value. */}
      <div className="flex items-start gap-1 rounded-md border bg-muted/40 py-1 pl-3 pr-1">
        <span
          className={cn('min-w-0 flex-1 py-1.5 font-mono text-xs leading-4', showsClear ? 'break-all' : 'truncate')}
          aria-hidden={secret && !revealed}
        >
          {secret && !revealed ? MASK : value}
        </span>
        {secret && revealable ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="shrink-0"
            onClick={() => setRevealed((prev) => !prev)}
            aria-label={revealed ? `Hide ${inlineLabel(label)}` : `Reveal ${inlineLabel(label)}`}
          >
            {revealed ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="shrink-0"
          ref={copyButtonRef}
          onClick={handleCopy}
          aria-label={`Copy ${inlineLabel(label)}`}
        >
          {copied ? <Check aria-hidden="true" className="text-success-emphasis" /> : <Copy aria-hidden="true" />}
        </Button>
      </div>
    </div>
  );
};
